import React, { useRef, useState } from 'react';

import { withEventScope } from '@repo/event-processor';
import { Trans, useTranslation } from '@repo/i18n';
import UploadIcon from '@repo/ui/components/Icon/UploadIcon';
import { H5, P3 } from '@repo/ui/components/Typography';
import { themeStyle } from '@repo/ui/theming';

import {
  rasterImageTypes,
  getValidDataTransferItemFromDragEvent,
  hasValidDimensions,
} from 'global/utils/fileUtils';
import { FlexColumn } from 'styles/layouts';
import { HideDesktop, HideMobile } from 'styles/queries';

import ResponsiveSpacer from '../../ResponsiveSpacer/ResponsiveSpacer';

import { DragOverlay, InnerWrapper, OuterWrapper } from './styles';
import { FilePickerProps } from './types';

const FilePicker: React.FC<FilePickerProps> = ({
  disabled,
  onFileSelect,
  onDisabledClick,
}) => {
  const { t } = useTranslation();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [dragState, setDragState] = useState<'valid' | 'invalid' | null>(null);

  const handleClick = (): void => {
    if (disabled) {
      onDisabledClick();

      return;
    }

    fileInputRef.current?.click();
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = async (
    event
  ): Promise<void> => {
    if (disabled) {
      onDisabledClick();
    }

    const file = event.target.files?.[0];

    if (file && (await hasValidDimensions(file))) {
      onFileSelect(file);
    }
  };

  const handleDrop: React.DragEventHandler = async (event) => {
    event.preventDefault();

    setDragState(null);

    if (disabled) {
      onDisabledClick();

      return;
    }

    const file = getValidDataTransferItemFromDragEvent(event)?.getAsFile();

    if (file && (await hasValidDimensions(file))) {
      onFileSelect(file);
    }
  };

  const handleDragEnter: React.DragEventHandler = (event) => {
    event.preventDefault();

    if (disabled) {
      return;
    }

    setDragState(
      getValidDataTransferItemFromDragEvent(event) ? 'valid' : 'invalid'
    );
  };

  const handleDragLeave: React.DragEventHandler = (event) => {
    event.preventDefault();

    setDragState(null);
  };

  const handleDragOver: React.DragEventHandler = (event) => {
    event.preventDefault();

    if (disabled) {
      return;
    }

    event.dataTransfer.dropEffect = 'copy';
  };

  return (
    <OuterWrapper>
      <InnerWrapper className={dragState ?? ''} disabled={disabled}>
        {/*The overlay is needed to prevent children from triggering dragleave.*/}
        <DragOverlay
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        />
        <FlexColumn alignItems="center" height="100%" justifyContent="center">
          <UploadIcon
            ignoreHoverStyles
            height="80px"
            theme={{ color: themeStyle.inkLight }}
          />
          <ResponsiveSpacer dh="24px" />
          <H5 color={themeStyle.varInkMedium}>
            <HideMobile display="inline">
              <Trans
                components={[<u key="0" />]}
                i18nKey="ai:ai_tool.upload_image_text"
                values={{
                  uploadText: t(
                    'ai:ai_tool.mobile_upload_image_text'
                  ).toLowerCase(),
                }}
              />
            </HideMobile>
            <HideDesktop>
              <u>{t('ai:ai_tool.mobile_upload_image_text')}</u>
            </HideDesktop>
          </H5>
        </FlexColumn>
        <input
          ref={fileInputRef}
          hidden
          accept={rasterImageTypes.join(',')}
          type="file"
          onChange={handleChange}
          onClick={(event): void => {
            (event.target as HTMLInputElement).value = '';
          }}
        />
        <ResponsiveSpacer dh="107px" mh="58px" />
        <P3 color={themeStyle.varInkMedium}>
          {t('ai:ai_tool.supported_file_types_text')}
        </P3>
      </InnerWrapper>
    </OuterWrapper>
  );
};

export default withEventScope(FilePicker, { module: 'filePicker' });
